import { useNavigate } from 'react-router-dom';
import { ArrowLeft, GlassWater, Sparkles, Citrus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function MocktailMixologyGuide() {
  const navigate = useNavigate();

  const recipes = [
    {
      name: "Berry Sparkler",
      ingredients: ["Sparkling water", "Muddled raspberries or blueberries", "Fresh mint", "Squeeze of lime", "Ice"],
      instructions: "Muddle berries with mint in glass, add ice, top with sparkling water and lime."
    },
    {
      name: "Ginger Mule",
      ingredients: ["Ginger beer (non-alcoholic)", "Fresh lime juice", "Dash of bitters (optional)", "Lime wedge", "Ice"],
      instructions: "Fill glass with ice, add ginger beer and lime juice, stir, garnish with lime."
    },
    {
      name: "Cucumber Cooler",
      ingredients: ["Sparkling water", "Cucumber slices", "Fresh basil", "Lemon juice", "Honey or agave"],
      instructions: "Muddle cucumber and basil, add sweetener, top with sparkling water and lemon."
    },
    {
      name: "Citrus Sunset",
      ingredients: ["Orange juice", "Cranberry juice", "Splash of grenadine", "Sparkling water", "Orange slice"],
      instructions: "Layer orange juice, cranberry, and a splash of grenadine. Top with sparkling water."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
            <GlassWater className="w-8 h-8 text-pink-600" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">Mocktail Mixology</h1>
          <p className="text-lg text-secondary-600">Create sophisticated non-alcoholic drinks</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Mocktails Matter</h2>
          <p className="text-secondary-700 mb-4">
            Having appealing alternatives makes socializing easier. A well-crafted mocktail removes the feeling of deprivation and proves that special drinks don't require alcohol. It's about pleasure, not punishment.
          </p>
          <div className="bg-pink-50 rounded-xl p-4 border border-pink-200">
            <p className="text-pink-800 font-medium">
              🥂 Pro tip: Serve mocktails in nice glassware with garnishes. The experience matters as much as the taste!
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-pink-500" />
            Recipes to Try
          </h2>
          <div className="space-y-6">
            {recipes.map((recipe, i) => (
              <div key={i} className="p-6 bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl border border-pink-200">
                <h3 className="text-xl font-bold text-secondary-900 mb-3">{recipe.name}</h3>
                <div className="mb-3">
                  <p className="font-medium text-secondary-800 mb-2">Ingredients:</p>
                  <ul className="list-disc list-inside text-secondary-600 text-sm space-y-1">
                    {recipe.ingredients.map((ing, j) => (
                      <li key={j}>{ing}</li>
                    ))}
                  </ul>
                </div>
                <p className="text-secondary-700 text-sm"><span className="font-medium">How to make:</span> {recipe.instructions}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-pink-50 to-amber-50 rounded-2xl p-8 border-2 border-pink-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Citrus className="w-6 h-6 text-amber-500" />
            Today's Challenge
          </h2>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="bg-pink-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</span>
              <span className="text-secondary-700">Choose a recipe above or create your own</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-pink-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</span>
              <span className="text-secondary-700">Gather ingredients (substitute freely based on what you have)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-pink-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</span>
              <span className="text-secondary-700">Serve in your nicest glass with a garnish</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-pink-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">4</span>
              <span className="text-secondary-700">Sip slowly and enjoy mindfully</span>
            </li>
          </ol>
        </section>

        <section className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-2">📝 Journal Prompt</h3>
          <p className="text-amber-900">
            "What mocktail did I make? How did it feel to have a 'special' drink without alcohol? Would I make this again?"
          </p>
        </section>
      </main>
    </div>
  );
}

export default MocktailMixologyGuide;
